import { useMemo, useState } from 'react'

type TabId = 'memory' | 'identity' | 'payments'

type Tab = {
  id: TabId
  label: string
  title: string
  desc: string
  bullets: string[]
}

const tabs: Tab[] = [
  {
    id: 'memory',
    label: 'Memory',
    title: 'Make lived experience portable',
    desc: 'Personality memory is not chat logs. It is a private, searchable set of takeaways you can control and update over time.',
    bullets: ['Encrypted by default', 'Smart search', 'Controlled recall'],
  },
  {
    id: 'identity',
    label: 'Identity',
    title: 'Wallet-centered, verifiable identity',
    desc: 'Identity is more than a session. It defines verifiable capabilities: quotas, permissions, tiers, and credentials.',
    bullets: ['Wallet signatures and sessions', 'Tiered benefits and access', 'Extensible proofs and credentials'],
  },
  {
    id: 'payments',
    label: 'Payments',
    title: 'From 402 to settlement, then receipt',
    desc: 'Payments are not a single transfer. They are a negotiable, observable, and auditable settlement path.',
    bullets: ['x402 compatibility + normalization', 'Negotiation engine with guardrails', 'ExecutionPlan + Receipt'],
  },
]

export default function Showcase() {
  const [active, setActive] = useState<TabId>('payments')
  const tab = useMemo(() => tabs.find((t) => t.id === active) ?? tabs[0], [active])

  return (
    <section className="bg-bg">
      <div className="container-x py-14 md:py-18">
        <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div>
            <h2 className="text-2xl font-semibold tracking-tight md:text-3xl">What we're building</h2>
            <p className="mt-2 max-w-3xl text-white/65">
              Three pillars reinforce each other: memory makes experiences reusable, identity makes authorization verifiable,
              and payments make promises deliverable.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {tabs.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setActive(t.id)}
                className={[
                  'rounded-full px-4 py-2 text-sm font-semibold transition',
                  t.id === active
                    ? 'bg-primary text-black'
                    : 'border border-white/10 bg-white/5 text-white/80 hover:bg-white/10 hover:text-white',
                ].join(' ')}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-8 grid gap-5 md:grid-cols-12">
          <div className="glass p-7 md:col-span-7">
            <div className="text-lg font-semibold text-white/90">{tab.title}</div>
            <div className="mt-2 text-sm leading-relaxed text-white/65">{tab.desc}</div>
            <div className="mt-6 grid gap-3 md:grid-cols-3">
              {tab.bullets.map((b) => (
                <div key={b} className="rounded-xl border border-white/10 bg-white/5 p-4 text-sm text-white/75">
                  {b}
                </div>
              ))}
            </div>
            <div className="mt-7 rounded-xl border border-white/10 bg-black/20 p-4">
              <div className="text-xs text-white/55">Why now</div>
              <div className="mt-2 text-sm text-white/70">
                Multi-chain experiences already exist, but the settlement layer is still primitive: expensive, opaque, and hard to audit.
                We are systematizing it into infrastructure.
              </div>
            </div>
          </div>
          <div className="glass2 p-7 md:col-span-5">
            <div className="text-sm font-semibold text-white/90">What we ship in public</div>
            <div className="mt-4 grid gap-3">
              <MiniCard label="Pace" value="Shipping weekly" hint="Each phase has a runnable demo" />
              <MiniCard label="Quality" value="Regression-safe" hint="Unit / e2e / performance gates" />
              <MiniCard label="Security" value="Least privilege" hint="CSP + dependency audit + no secrets in clients" />
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

function MiniCard(props: { label: string; value: string; hint: string }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/5 p-4">
      <div className="text-xs text-white/55">{props.label}</div>
      <div className="mt-1 text-base font-semibold text-white/90">{props.value}</div>
      <div className="mt-1 text-xs text-white/45">{props.hint}</div>
    </div>
  )
}
